import Decimal from 'decimal.js';
import { Bill, Invoice, InvoiceLine } from './invoicing';
import { InvoiceUtility } from '../../utilities/invoice.utility';
import { TaxInclusiveOption } from '../purchase/purchase.order.line';
import { TaxRate } from '../taxrate/tax.rate';

/**
 * Named Invoice / Bill line calculator (EF V2 production contract).
 * Shared by Surface recompute and server totals validation. Distinct from `InvoiceCalculationDomain`,
 * which owns payment amounts (paid / outstanding / credit), not line money.
 *
 * Rules:
 * - Tax modes: Exclusive, Inclusive, No Tax (untaxed: line total stays the line subtotal)
 * - Line tax rate falls back to the document rate when the line carries none, except a consolidated
 *   job line (links, no rate), which keeps its stored tax
 * - Invoice only: discount is clamped to the line gross (Value) or to 100 (Percent) before totalling
 * - Rounding: unit price 4dp, line money 2dp, document totals 2dp
 * - Source model and its lines are never mutated
 *
 * Line `_id` minting is deliberately NOT here: a fresh uuid is per-call state creation, not money
 * math, and would make the calculator non-deterministic against the server's totals validation.
 */
export type InvoiceLineCalculationSource = Pick<Invoice | Bill, 'lines' | 'taxInclusiveOption' | 'taxRate'>;

export interface InvoiceLineCalculationResult {
  lines: InvoiceLine[];
  subTotal: number;
  tax: number;
  total: number;
  /** The mode the totals were calculated under. Reported so a document with none stored adopts it. */
  taxInclusiveOption: TaxInclusiveOption;
}

export class InvoiceLineCalculationDomain {

  static calculateInvoice(source: InvoiceLineCalculationSource): InvoiceLineCalculationResult {

    return calculateInvoice(source);
  }

  static calculateBill(source: InvoiceLineCalculationSource): InvoiceLineCalculationResult {

    return calculateBill(source);
  }
}

export function calculateInvoice(source: InvoiceLineCalculationSource): InvoiceLineCalculationResult {

  return calculateLines(source, true);
}

export function calculateBill(source: InvoiceLineCalculationSource): InvoiceLineCalculationResult {

  // A bill's supplier states the discount; there is no gross/percent clamp to defend against.
  return calculateLines(source, false);
}

// Invoice and bill differ only in whether the entered discount is clamped, so the shared core takes
// that as its single flag rather than being split into two near-identical passes.
function calculateLines(source: InvoiceLineCalculationSource, clampDiscount: boolean): InvoiceLineCalculationResult {

  const taxRate = source?.taxRate as TaxRate | undefined;
  const taxMode = source?.taxInclusiveOption ?? TaxInclusiveOption.Exclusive;
  // Copies, because getInvoiceLineTotal writes subTotal/tax/total onto the lines it is handed.
  const lines = (source?.lines ?? []).map(line => ({ ...line })) as InvoiceLine[];

  for (const line of lines) {
    // A consolidated line has no single rate by design; giving it the document's re-taxes its summed lines.
    if (!line.taxRate && taxRate && !InvoiceUtility.isConsolidatedLine(line)) line.taxRate = taxRate;
    if (!clampDiscount) continue;
    // A percent above 100 is not a discount the line can apply. A dollar discount is left as typed:
    // the line total already refuses to go past zero, and writing the cap back replaces what the
    // user entered. Typing the unit price one digit at a time was cutting $500 down to $4.
    if (line.discountType === 'Percent' && Number(line.discount ?? 0) > 100) line.discount = 100;
  }

  const result = InvoiceUtility.getInvoiceLineTotal(lines, taxRate as TaxRate, taxMode);

  return {
    // Utility return type unions PO lines; this path always passes InvoiceLine[].
    lines: result.lines as InvoiceLine[],
    // getInvoiceLineTotal aggregates at 4dp so line rounding survives the sum; the document shows 2dp.
    subTotal: round2(result.subTotal),
    tax: round2(result.tax),
    total: round2(result.total),
    taxInclusiveOption: taxMode,
  };
}

function round2(value: number): number {

  return new Decimal(value ?? 0).toDP(2).toNumber();
}
